import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Footer from '../components/Footer';
import { ArrowLeft, Cookie, Shield, BarChart3, Settings as SettingsIcon } from 'lucide-react';

const COOKIE_TABLE = [
  {
    category: 'Strictly Necessary',
    icon: Shield,
    required: true,
    description: 'Essential for the site to function. Cannot be disabled.',
    cookies: [
      { name: 'vibesprint_workspace_id', purpose: 'Remember your selected workspace between visits', duration: 'Persistent (localStorage)', provider: 'VibeSprint' },
      { name: 'vibesprint_consent', purpose: 'Remember your cookie consent choice', duration: '12 months', provider: 'VibeSprint' },
      { name: 'session', purpose: 'Maintain your session while using the dashboard', duration: 'Session', provider: 'VibeSprint' },
    ],
  },
  {
    category: 'Analytics',
    icon: BarChart3,
    required: false,
    description: 'Help us understand how visitors use the site so we can improve it. Anonymous — we never link analytics to individual users.',
    cookies: [
      { name: '_plausible', purpose: 'Anonymous page-view and conversion tracking', duration: 'Session', provider: 'Plausible Analytics (planned)' },
    ],
  },
  {
    category: 'Preferences',
    icon: SettingsIcon,
    required: false,
    description: 'Remember UI choices like dashboard layout, sort order, and filters.',
    cookies: [
      { name: 'vibesprint_sort_pref', purpose: 'Remember your default sort order on the dashboard', duration: 'Persistent (localStorage)', provider: 'VibeSprint' },
      { name: 'vibesprint_theme', purpose: 'Remember light/dark theme choice (planned)', duration: 'Persistent (localStorage)', provider: 'VibeSprint' },
    ],
  },
];

const CookiePolicyPage = () => {
  const navigate = useNavigate();

  const handleManagePreferences = () => {
    // Clear consent so banner re-appears
    localStorage.removeItem('vibesprint_consent');
    localStorage.removeItem('vibesprint_consent_choices');
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex h-16 items-center justify-between">
            <a href="/" className="flex items-center gap-2" data-testid="cookies-logo-link">
              <span className="text-xl font-bold text-foreground">VibeSprint</span>
              <span className="text-[10px] px-1.5 py-0 border border-orange-500/50 text-orange-600 uppercase tracking-wider font-semibold rounded-md">Alpha</span>
            </a>
            <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2" data-testid="cookies-back-button">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-12 max-w-4xl">
        <div className="flex items-center gap-3 mb-3">
          <div className="rounded-full bg-primary/10 p-2.5">
            <Cookie className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-4xl font-bold">Cookie Policy</h1>
        </div>
        <p className="text-muted-foreground mb-10">Last updated: January 2026</p>

        <div className="space-y-10">
          <section>
            <h2 className="text-2xl font-semibold mb-3">1. What are cookies?</h2>
            <p className="text-muted-foreground leading-relaxed">
              Cookies are small text files stored on your device when you visit a website. They help the site remember your preferences (like which workspace you had open) and tell us how the site is being used so we can improve it. We also use <code className="px-1 py-0.5 rounded bg-muted text-xs">localStorage</code>, which works similarly but stores data more persistently. For simplicity, we refer to all of these as "cookies" on this page.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">2. How we use cookies</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We keep cookie usage as minimal as possible. We do <strong>not</strong> use advertising cookies, tracking pixels, or third-party social media trackers. Every cookie we set falls into one of three categories below.
            </p>
          </section>

          {/* Cookie category cards */}
          {COOKIE_TABLE.map((cat) => {
            const Icon = cat.icon;
            return (
              <section key={cat.category} data-testid={`cookie-category-${cat.category.toLowerCase().replace(/\s+/g, '-')}`}>
                <Card className="p-6">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="rounded-lg bg-primary/10 p-2">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold">{cat.category}</h3>
                        <p className="text-sm text-muted-foreground mt-0.5">{cat.description}</p>
                      </div>
                    </div>
                    <Badge variant={cat.required ? 'default' : 'outline'} className="flex-shrink-0">
                      {cat.required ? 'Always active' : 'Optional'}
                    </Badge>
                  </div>

                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left px-4 py-2.5 font-medium">Cookie</th>
                          <th className="text-left px-4 py-2.5 font-medium">Purpose</th>
                          <th className="text-left px-4 py-2.5 font-medium hidden md:table-cell">Duration</th>
                          <th className="text-left px-4 py-2.5 font-medium hidden md:table-cell">Provider</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cat.cookies.map((c, i) => (
                          <tr key={c.name} className={i > 0 ? 'border-t' : ''}>
                            <td className="px-4 py-2.5 font-mono text-xs">{c.name}</td>
                            <td className="px-4 py-2.5 text-muted-foreground">{c.purpose}</td>
                            <td className="px-4 py-2.5 text-muted-foreground hidden md:table-cell">{c.duration}</td>
                            <td className="px-4 py-2.5 text-muted-foreground hidden md:table-cell">{c.provider}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </section>
            );
          })}

          <section>
            <h2 className="text-2xl font-semibold mb-3">3. Managing your preferences</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              You can change your mind at any time. Click the button below to re-open the consent banner and update your choices. You can also clear cookies via your browser settings.
            </p>
            <Button
              variant="outline"
              onClick={handleManagePreferences}
              data-testid="cookies-manage-preferences"
            >
              <SettingsIcon className="h-4 w-4 mr-2" />
              Manage cookie preferences
            </Button>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">4. Third-party cookies</h2>
            <p className="text-muted-foreground leading-relaxed mb-2">
              We may use the following third-party services, each of which may set their own cookies:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-1.5">
              <li><strong>Plausible Analytics</strong> (planned) — privacy-focused, GDPR-compliant analytics. No personal data collected, no cross-site tracking.</li>
              <li><strong>Stripe</strong> (when payments launch) — to securely process card transactions on the pricing page.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              We deliberately do not use Google Analytics, Facebook Pixel, advertising trackers, or third-party social widgets.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">5. Your rights (EU / UK / California)</h2>
            <p className="text-muted-foreground leading-relaxed">
              Under GDPR (EU), UK-GDPR, and CCPA (California), you have the right to access, correct, delete, and port your data. To exercise any of these rights, email <a href="mailto:support@vibesprint.ai" className="text-primary hover:underline">support@vibesprint.ai</a> and we'll respond within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">6. Contact</h2>
            <p className="text-muted-foreground leading-relaxed">
              Questions about this policy? Email <a href="mailto:support@vibesprint.ai" className="text-primary hover:underline">support@vibesprint.ai</a>.
            </p>
          </section>
        </div>
      </main>

      <Footer variant="minimal" />
    </div>
  );
};

export default CookiePolicyPage;
